import React, { useEffect, useState } from 'react';
import researchService from '../../services/researchService';

const EXT_IMAGE = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

/**
 * Lightweight modal that previews a Bedrock KB source by its S3 URI.
 *
 * Props:
 *   s3Uri    – full S3 URI (e.g. s3://hff-research/files/...)
 *   filename – display name (extracted from the URI upstream)
 *   onClose  – callback to dismiss
 */
export default function SourcePreviewModal({ s3Uri, filename, sourceUrl, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState(null);

  const ext = (filename || '').split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const isImage = EXT_IMAGE.includes(ext);
  const isHtml = ext === 'htm' || ext === 'html';
  const isText = ext === 'txt';

  const formatTimestampToEastern = (timestamp) => {
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) return timestamp;

    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(parsed);
  };

  const renderTextWithClickableLinks = (text) => {
    const safeText = String(text || '');
    const urlRegex = /(https?:\/\/[^\s<>")\]}]+)/g;
    const parts = safeText.split(urlRegex);

    return parts.map((part, idx) => {
      if (/^https?:\/\//i.test(part)) {
        return (
          <a
            key={`url-${idx}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        );
      }
      return <React.Fragment key={`txt-${idx}`}>{part}</React.Fragment>;
    });
  };

  const renderFormattedTextContent = (text) => {
    const lines = String(text || '').split('\n');

    return lines.map((line, idx) => {
      const match = line.match(/^\[([^\]]+)\]\s+(https?:\/\/\S+)(.*)$/);

      if (match) {
        const [, rawTimestamp, postUrl, trailing] = match;
        const formattedTs = formatTimestampToEastern(rawTimestamp);

        return (
          <React.Fragment key={`line-${idx}`}>
            <span className="text-muted">[{formattedTs}] </span>
            <a href={postUrl} target="_blank" rel="noopener noreferrer">Actual Post</a>
            {trailing ? <>{renderTextWithClickableLinks(trailing)}</> : null}
            {idx < lines.length - 1 ? '\n' : null}
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={`line-${idx}`}>
          {renderTextWithClickableLinks(line)}
          {idx < lines.length - 1 ? '\n' : null}
        </React.Fragment>
      );
    });
  };

  useEffect(() => {
    if (!s3Uri) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { url: presigned } = await researchService.presignSource(s3Uri);
        if (!cancelled) setUrl(presigned);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [s3Uri]);

  useEffect(() => {
    if (!url || !isText) {
      setTextContent('');
      setTextError(null);
      setTextLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      setTextLoading(true);
      setTextError(null);
      try {
        const resp = await fetch(url, { signal: controller.signal });
        if (!resp.ok) {
          throw new Error(`Failed to load text preview (${resp.status})`);
        }
        const text = await resp.text();
        if (!cancelled) setTextContent(text);
      } catch (e) {
        if (!cancelled) setTextError(e.message || 'Failed to render text preview');
      } finally {
        if (!cancelled) setTextLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [url, isText]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!s3Uri) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1055,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        className="bg-white rounded shadow-lg d-flex flex-column"
        style={{ width: '90vw', maxWidth: 1100, height: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <span className="fw-bold text-truncate" style={{ maxWidth: '80%' }}>{filename}</span>
          <div className="d-flex gap-2">
            {url && (
              <a
                className="btn btn-sm btn-outline-secondary"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-box-arrow-up-right me-1" />Open in New Tab
              </a>
            )}
            {url && (
              <a
                className="btn btn-sm btn-outline-secondary"
                href={url}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-download me-1" />Download
              </a>
            )}
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>
        {sourceUrl && (
          <div className="px-3 py-2 border-bottom bg-light">
            <span className="small text-muted me-2">Source URL:</span>
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="small"
              title={sourceUrl}
            >
              {sourceUrl}
            </a>
          </div>
        )}

        {/* Body */}
        <div className="flex-grow-1 overflow-hidden position-relative">
          {loading && (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="spinner-border text-primary" />
            </div>
          )}

          {!loading && error && (
            <div className="d-flex align-items-center justify-content-center h-100 text-danger">
              <i className="bi bi-exclamation-triangle me-2" />{error}
            </div>
          )}

          {!loading && !error && url && isPdf && (
            <iframe
              src={url}
              title={filename}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}

          {!loading && !error && url && isHtml && (
            <iframe
              src={url}
              title={filename}
              sandbox=""
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}

          {!loading && !error && url && isImage && (
            <div className="d-flex align-items-center justify-content-center h-100 overflow-auto p-3">
              <img src={url} alt={filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
          )}

          {!loading && !error && url && isText && (
            <div className="h-100 overflow-auto p-3 bg-white">
              {textLoading && (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="spinner-border text-primary" />
                </div>
              )}
              {!textLoading && textError && (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-2 text-danger">
                  <div><i className="bi bi-exclamation-triangle me-2" />{textError}</div>
                  <a
                    className="btn btn-primary"
                    href={url}
                    download={filename}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="bi bi-download me-1" />Download
                  </a>
                </div>
              )}
              {!textLoading && !textError && (
                <div
                  className="mb-0"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem', fontFamily: 'monospace' }}
                >
                  {renderFormattedTextContent(textContent)}
                </div>
              )}
            </div>
          )}

          {!loading && !error && url && !isPdf && !isHtml && !isImage && !isText && (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3">
              <i className="bi bi-file-earmark fs-1 text-muted" />
              <p className="text-muted mb-0">Preview not available for this file type.</p>
              <a
                className="btn btn-primary"
                href={url}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-download me-1" />Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
